import { useState } from 'react';
import {
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type { DeliveriesDeliveryFiltersResponse, DeliveriesDeliveryStatus } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { newItemsCheckOptions } from '../shared/new-items-check';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { carryDetailPendingFlags, carryPendingFlags } from './cache';
import { deliveryKeys, parseDelivery, type Delivery, type DeliveriesListParams, type DeliveryDetail } from './entity';

/**
 * Delivery reads. The Outbox is a live log — Ember re-runs `store.query` on
 * every filter change and appends on load-more — so the list uses
 * `staleTime: 0` and continuation-based `useInfiniteQuery`.
 */

const LIST_STALE_TIME = 0;
const FILTERS_STALE_TIME = 60_000;
const PAGE_SIZE = 50;

export interface DeliveriesPage {
  deliveries: readonly Delivery[];
  continuation: string;
}

/** One page of deliveries, with no cache writes. An empty `continuation` reads the first page. */
async function fetchDeliveriesPage(
  workspaceId: number,
  params: DeliveriesListParams,
  continuation: string,
  signal?: AbortSignal,
): Promise<DeliveriesPage> {
  const data = await hydraUiApi.deliveries.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { ...params, size: PAGE_SIZE, continuation: isEmptyString(continuation) ? undefined : continuation },
    },
    signal,
  });
  const deliveries = (data.deliveries ?? []).map(parseDelivery);
  return { deliveries, continuation: data.meta?.continuation ?? '' };
}

/** @public */
export async function getDeliveriesPage(
  workspaceId: number,
  params: DeliveriesListParams,
  continuation: string,
  qc: QueryClient,
): Promise<DeliveriesPage> {
  const page = await fetchDeliveriesPage(workspaceId, params, continuation);
  // Sideload each row into its detail slot so a row-detail open resolves from cache.
  for (const d of page.deliveries) {
    const key = deliveryKeys.detail(workspaceId, d.id);
    qc.setQueryData(key, carryDetailPendingFlags(qc.getQueryData<Delivery>(key), d));
  }
  return page;
}

/**
 * A person's few most recent sent deliveries, newest first, for a profile's overview. One page of
 * `size`, cached apart from the paginated list so the two never share pages.
 */
export const personRecentDeliveriesQueryOptions = (workspaceId: number, internalId: string, size: number) =>
  queryOptions({
    queryKey: [...deliveryKeys.lists(workspaceId), 'person-recent', internalId, size] as const,
    queryFn: async ({ signal }) => {
      const data = await hydraUiApi.deliveries.list({
        params: { path: { environment_id: String(workspaceId) }, query: { internal_id: internalId, size } },
        signal,
      });
      return (data.deliveries ?? []).map(parseDelivery);
    },
    staleTime: 0,
  });

interface UseDeliveriesOptions {
  params?: DeliveriesListParams;
  /** `refetchInterval` reloads every loaded page on a timer (the newsletter Sent tab's auto refresh). */
  queryConfig?: { enabled?: boolean; refetchInterval?: number | false };
}

/**
 * Infinite, continuation-paginated deliveries list. Returns the flattened rows
 * plus load-more controls (`hasMore` / `fetchMore` / `isFetchingMore`) and
 * `lastUpdated`, mirroring the Ember controller's `fetchDeliveries` /
 * `fetchMore` / `lastUpdated`.
 *
 * @public
 */
export const useDeliveries = ({ params = {}, queryConfig }: UseDeliveriesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: deliveryKeys.list(workspaceId, params),
    queryFn: async ({ pageParam }) => {
      const page = await getDeliveriesPage(workspaceId, params, pageParam, qc);
      // A refetch must not drop the optimistic flags that bulk actions set (see `carryPendingFlags`).
      const previous = qc.getQueryData<InfiniteData<DeliveriesPage>>(deliveryKeys.list(workspaceId, params));
      return { ...page, deliveries: carryPendingFlags(previous, page.deliveries) };
    },
    initialPageParam: '',
    getNextPageParam: (last) => (isEmptyString(last.continuation) ? undefined : last.continuation),
    staleTime: LIST_STALE_TIME,
    enabled: queryConfig?.enabled,
    refetchInterval: queryConfig?.refetchInterval,
    // Ember's auto refresh loop keeps running in a background tab, so the timer does too.
    refetchIntervalInBackground: true,
  });

  const deliveries = query.data?.pages.flatMap((page) => page.deliveries) ?? [];
  const isListError = useIsListError(query);

  return {
    ...query,
    deliveries,
    hasMore: query.hasNextPage,
    fetchMore: query.fetchNextPage,
    isFetchingMore: query.isFetchingNextPage,
    isFetchMoreError: query.isFetchNextPageError,
    /** The list itself failed, so it shows its error state (see `useIsListError`). */
    isListError,
    lastUpdated: query.dataUpdatedAt,
    /** The newest page, which seeds the "load new" check. */
    firstPage: query.data?.pages[0],
  };
};

type ListFailure = 'list' | 'next-page';

type ListErrorState = { isError: boolean; isFetching: boolean; isFetchNextPageError: boolean };

/**
 * Whether the list itself failed, rather than only its next page. A failed next page errors the
 * whole query too, and React Query forgets which fetch failed once the next one starts. So this
 * remembers which kind failed last: after a failed next page, a refetch keeps the rows on screen;
 * after a failed list, the error state holds through the retry rather than flashing the old rows.
 */
function useIsListError({ isError, isFetching, isFetchNextPageError }: ListErrorState): boolean {
  const [lastFailure, setLastFailure] = useState<ListFailure | null>(null);
  const failure: ListFailure | null = isFetchNextPageError ? 'next-page' : isError && !isFetching ? 'list' : null;
  if (failure !== null && failure !== lastFailure) setLastFailure(failure);
  const latest = failure ?? lastFailure;
  return isError && !isFetchNextPageError && latest === 'list';
}

interface UseNewDeliveriesCheckOptions {
  /** The same list params as the table, so the check counts only deliveries the table would show. */
  params: DeliveriesListParams;
  /** False stops the check, for example while the list is still loading. */
  enabled: boolean;
  /** The list's first page and when it was fetched, so the page loads once. */
  seed: DeliveriesPage | undefined;
  seedUpdatedAt: number;
}

/**
 * The "load new" check for the deliveries list: the first page every 15s, on its own key, with the
 * same params as the list. See `newItemsCheckOptions`. It reads without writing detail slots, so it
 * does not churn the cache or overwrite the `pending*` flags that bulk actions set on them, and it
 * cancels an in-flight read when the params change.
 *
 * @public
 */
export function useNewDeliveriesCheck({ params, enabled, seed, seedUpdatedAt }: UseNewDeliveriesCheckOptions) {
  const workspaceId = requireWorkspaceId();
  return useQuery(
    newItemsCheckOptions({
      queryKey: deliveryKeys.newCheck(workspaceId, params),
      queryFn: ({ signal }) => fetchDeliveriesPage(workspaceId, params, '', signal),
      enabled,
      seed,
      seedUpdatedAt,
    }),
  );
}

/** @public */
export async function getDelivery(workspaceId: number, id: string): Promise<Delivery> {
  const data = await hydraUiApi.deliveries.get({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (data.delivery == null) {
    throw new Error('delivery response did not include a delivery');
  }
  return parseDelivery(data.delivery);
}

/** @public */
export const getDeliveryQueryOptions = (workspaceId: number, id: string) =>
  queryOptions({
    queryKey: deliveryKeys.detail(workspaceId, id),
    queryFn: async ({ client }) => {
      const fresh = await getDelivery(workspaceId, id);
      // A refetch must not drop the optimistic flags that bulk actions set (see `carryDetailPendingFlags`).
      return carryDetailPendingFlags(client.getQueryData<Delivery>(deliveryKeys.detail(workspaceId, id)), fresh);
    },
    staleTime: LIST_STALE_TIME,
  });

/** @public */
export const useDelivery = ({
  id,
  queryConfig,
}: {
  id: string;
  queryConfig?: QueryConfig<typeof getDeliveryQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getDeliveryQueryOptions(workspaceId, id), ...queryConfig });
};

/**
 * The delivery's resend-chain siblings (`related_deliveries`), each read by id.
 * Each sibling is fetched independently, and a sibling whose read fails is
 * dropped while the rest still render (Ember `Promise.allSettled` + filter) — a
 * deleted or inaccessible sibling must not blank out the panel. `isLoading`
 * reports until every sibling read has settled, so the rows appear together.
 *
 * @public
 */
export const useRelatedDeliveries = (ids: readonly string[]) => {
  const workspaceId = requireWorkspaceId();
  const results = useQueries({
    queries: ids.map((id) => ({ ...getDeliveryQueryOptions(workspaceId, id), retry: false })),
  });
  return {
    deliveries: results.flatMap((result) => (result.isSuccess ? [result.data] : [])),
    isLoading: results.some((result) => result.isPending),
  };
};

/**
 * One detail read: the rendered content, and the delivery's recorded status. The detail endpoint
 * reads that status from every metric, archived ones included, so it can carry a timestamp the list
 * row lacks: a failed delivery's `attempted` lives only in the list's `archived_metrics`.
 */
export type DeliveryDetailRead = {
  detail: DeliveryDetail;
  status: DeliveriesDeliveryStatus;
};

/**
 * The rendered delivery detail (`/delivery_details/:id`), with the delivery's status. The `edit`
 * variant (`/delivery_details/:id/edit`) returns the original source with unwrapped tracking links,
 * for drafts being edited.
 *
 * @public
 */
export async function getDeliveryDetailRead(workspaceId: number, id: string): Promise<DeliveryDetailRead> {
  const data = await hydraUiApi.deliveries.getDeliveryDetails({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (data.delivery_detail == null) {
    throw new Error('delivery detail response did not include a delivery_detail');
  }
  return { detail: { ...data.delivery_detail, contentSource: data.content_source }, status: data.delivery };
}

const selectDetail = (read: DeliveryDetailRead) => read.detail;

/** @public */
export async function getDeliveryDetailEdit(workspaceId: number, id: string): Promise<DeliveryDetail> {
  const data = await hydraUiApi.deliveries.getDeliveryDetailsEdit({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (data.delivery_detail == null) {
    throw new Error('delivery detail (edit) response did not include a delivery_detail');
  }
  return data.delivery_detail;
}

/**
 * The delivery detail page's read of the rendered content. It does not suspend: a failed read
 * must leave the rest of the page standing (Ember `cio-delivery-detail` catches the load and
 * renders with no detail), so callers read `data` as absent on error. `meta.errorMessage` puts
 * Ember's critical snackbar up through the shared query-error pipeline, once per failed fetch.
 *
 * @public
 */
export const useDeliveryDetailContent = ({ id }: { id: string }) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    queryKey: deliveryKeys.detailContent(workspaceId, id),
    queryFn: () => getDeliveryDetailRead(workspaceId, id),
    select: selectDetail,
    staleTime: LIST_STALE_TIME,
    meta: { errorMessage: "We weren't able to load the delivery detail" },
  });
};

/**
 * Non-suspending detail read for the expanded row. The row shows the delivery's
 * error messages, gates Resend on `hide_body`, and takes the status timestamps
 * the list row lacks (see `DeliveryDetailRead`); a detail-fetch
 * failure must degrade gracefully (leave those absent) rather than tear down
 * the row, so this tolerates errors instead of suspending. `enabled: false`
 * keeps it dormant until the row expands.
 *
 * @public
 */
export const useDeliveryDetailContentOptional = ({ id, enabled = true }: { id: string; enabled?: boolean }) => {
  const workspaceId = requireWorkspaceId();
  const active = enabled && id !== '';
  return useQuery({
    queryKey: active ? deliveryKeys.detailContent(workspaceId, id) : ['delivery', 'detail-content', 'inactive'],
    queryFn: active ? () => getDeliveryDetailRead(workspaceId, id) : skipToken,
    staleTime: LIST_STALE_TIME,
    retry: false,
  });
};

/** @public */
export async function getDeliveryFilters(workspaceId: number): Promise<DeliveriesDeliveryFiltersResponse> {
  return hydraUiApi.deliveries.getFilters({
    params: { path: { environment_id: String(workspaceId) } },
  });
}

/** @public */
export const useDeliveryFilters = () => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    queryKey: deliveryKeys.filters(workspaceId),
    queryFn: () => getDeliveryFilters(workspaceId),
    staleTime: FILTERS_STALE_TIME,
  });
};

/**
 * Non-suspense read of the same filters catalog, for controls that render a
 * loading state instead of suspending the page (the condition builder's
 * message picker).
 *
 * @public
 */
export const useDeliveryFiltersOptional = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    queryKey: deliveryKeys.filters(workspaceId),
    queryFn: () => getDeliveryFilters(workspaceId),
    staleTime: FILTERS_STALE_TIME,
    enabled,
  });
};
